import type { Dict } from '../../i18n';

const dict: Dict = {
  en: {
    // ── Topbar ──────────────────────────────────────────────────────────────
    heading: 'Users',
    summary: '{{total}} total · {{active}} active · {{blocked}} blocked · {{invited}} invite pending',
    search_placeholder: 'Search name or email…',
    btn_invite: '+ Invite user',

    // ── Table headers ────────────────────────────────────────────────────────
    th_user: 'User',
    th_role: 'Role',
    th_office: 'Office',
    th_status: 'Status',
    th_2fa: '2FA',
    th_joined: 'Joined',
    th_last_login: 'Last login',
    never: 'never',
    btn_edit: 'Edit',
    btn_delete: 'Delete user',
    btn_deleting: 'Deleting…',
    confirm_delete: 'Delete {{name}}? Their orders are reassigned. This cannot be undone.',

    // ── Status labels (table pills) ──────────────────────────────────────────
    status_active: 'active',
    status_blocked: 'blocked',
    status_invited: 'invited',

    // ── Loading / empty / error ───────────────────────────────────────────────
    loading_users: 'Loading users…',
    empty_title: 'No users yet',
    empty_hint: 'Invite a colleague to get started.',

    // ── User drawer ───────────────────────────────────────────────────────────
    drawer_close: 'Close',
    drawer_since: '· since',

    // Profile section
    section_profile: 'Name',
    first_name: 'First name',
    last_name: 'Last name',
    btn_save_name: 'Save name',
    btn_saving_name: 'Saving…',
    name_saved: 'Name updated.',

    // Role section
    section_role: 'Role',
    role_agent_locked: "An agent's role cannot be changed.",

    // Account section
    section_account: 'Account',
    btn_reset_password: 'Set temporary password',
    btn_resetting_password: 'Resetting password…',
    btn_reset_2fa: 'Reset 2FA',
    btn_resetting_2fa: 'Resetting 2FA…',
    tooltip_no_2fa: 'No 2FA enrolled yet',
    btn_block: 'Block user',
    btn_unblock: 'Unblock user',
    btn_blocking: 'Blocking…',
    btn_unblocking: 'Unblocking…',
    helper_block: 'Blocking revokes sessions and preserves history. Resets force a re-login.',

    // Prompt / confirm dialogs
    prompt_set_password: 'Set a temporary password for this user (they must change it):',
    confirm_reset_2fa: 'Reset 2FA? The user must re-enrol an authenticator on next login.',

    // Notice messages
    notice_password_reset: 'Password reset — the user’s sessions were revoked.',
    notice_2fa_reset: 'The user will re-enrol on next login.',

    // ── Invite drawer ─────────────────────────────────────────────────────────
    invite_heading: 'Add user',
    invite_close: 'Close',
    invite_label_fullname: 'Full name',
    invite_label_email: 'Email',
    invite_label_role: 'Role',
    invite_hint:
      'A temporary password is generated and shown once. The user sets their own password and enrols 2FA on first login.',
    btn_cancel: 'Cancel',
    btn_send_invite: 'Create user',
    btn_sending_invite: 'Creating…',
    invite_created_heading: 'User created',
    invite_created_hint: 'Share this temporary password with {{email}} — it is shown only once.',
    invite_temp_password: 'Temporary password',
    invite_temp_password_note:
      'The user must change it on first login and will be required to set up 2FA.',
    invite_done: 'Done',
  },

  es: {
    // ── Topbar ──────────────────────────────────────────────────────────────
    heading: 'Usuarios',
    summary:
      '{{total}} total · {{active}} activos · {{blocked}} bloqueados · {{invited}} invitación pendiente',
    search_placeholder: 'Buscar nombre o correo…',
    btn_invite: '+ Invitar usuario',

    // ── Table headers ────────────────────────────────────────────────────────
    th_user: 'Usuario',
    th_role: 'Rol',
    th_office: 'Oficina',
    th_status: 'Estado',
    th_2fa: '2FA',
    th_joined: 'Alta',
    th_last_login: 'Último acceso',
    never: 'nunca',
    btn_edit: 'Editar',
    btn_delete: 'Eliminar usuario',
    btn_deleting: 'Eliminando…',
    confirm_delete: '¿Eliminar a {{name}}? Sus pedidos se reasignan. No se puede deshacer.',

    // ── Status labels ────────────────────────────────────────────────────────
    status_active: 'activos',
    status_blocked: 'bloqueados',
    status_invited: 'invitados',

    // ── Loading / empty / error ───────────────────────────────────────────────
    loading_users: 'Cargando usuarios…',
    empty_title: 'Aún no hay usuarios',
    empty_hint: 'Invita a un compañero para empezar.',

    // ── User drawer ───────────────────────────────────────────────────────────
    drawer_close: 'Cerrar',
    drawer_since: '· desde',

    // Profile section
    section_profile: 'Nombre',
    first_name: 'Nombre',
    last_name: 'Apellidos',
    btn_save_name: 'Guardar nombre',
    btn_saving_name: 'Guardando…',
    name_saved: 'Nombre actualizado.',

    // Role section
    section_role: 'Rol',
    role_agent_locked: 'El rol de agente no se puede cambiar.',

    // Account section
    section_account: 'Cuenta',
    btn_reset_password: 'Establecer contraseña temporal',
    btn_resetting_password: 'Restableciendo contraseña…',
    btn_reset_2fa: 'Restablecer 2FA',
    btn_resetting_2fa: 'Restableciendo 2FA…',
    tooltip_no_2fa: 'Aún no hay 2FA vinculado',
    btn_block: 'Bloquear usuario',
    btn_unblock: 'Desbloquear usuario',
    btn_blocking: 'Bloqueando…',
    btn_unblocking: 'Desbloqueando…',
    helper_block:
      'Bloquear revoca las sesiones y conserva el historial. Los restablecimientos fuerzan un nuevo inicio de sesión.',

    // Prompt / confirm dialogs
    prompt_set_password: 'Establece una contraseña temporal para este usuario (debe cambiarla):',
    confirm_reset_2fa: '¿Restablecer 2FA? El usuario deberá vincular un autenticador en el próximo inicio de sesión.',

    // Notice messages
    notice_password_reset: 'Contraseña restablecida — se cerraron las sesiones del usuario.',
    notice_2fa_reset: 'El usuario lo vinculará de nuevo en el próximo inicio de sesión.',

    // ── Invite drawer ─────────────────────────────────────────────────────────
    invite_heading: 'Añadir usuario',
    invite_close: 'Cerrar',
    invite_label_fullname: 'Nombre completo',
    invite_label_email: 'Correo electrónico',
    invite_label_role: 'Rol',
    invite_hint:
      'Se genera una contraseña temporal que se muestra una sola vez. El usuario establece su propia contraseña y configura el 2FA en el primer inicio de sesión.',
    btn_cancel: 'Cancelar',
    btn_send_invite: 'Crear usuario',
    btn_sending_invite: 'Creando…',
    invite_created_heading: 'Usuario creado',
    invite_created_hint:
      'Comparte esta contraseña temporal con {{email}} — se muestra solo una vez.',
    invite_temp_password: 'Contraseña temporal',
    invite_temp_password_note:
      'El usuario debe cambiarla en el primer inicio de sesión y tendrá que configurar el 2FA.',
    invite_done: 'Hecho',
  },

  de: {
    // ── Topbar ──────────────────────────────────────────────────────────────
    heading: 'Benutzer',
    summary:
      '{{total}} gesamt · {{active}} aktiv · {{blocked}} gesperrt · {{invited}} Einladung ausstehend',
    search_placeholder: 'Name oder E-Mail suchen…',
    btn_invite: '+ Benutzer einladen',

    // ── Table headers ────────────────────────────────────────────────────────
    th_user: 'Benutzer',
    th_role: 'Rolle',
    th_office: 'Niederlassung',
    th_status: 'Status',
    th_2fa: '2FA',
    th_joined: 'Beigetreten',
    th_last_login: 'Letzte Anmeldung',
    never: 'nie',
    btn_edit: 'Bearbeiten',
    btn_delete: 'Benutzer löschen',
    btn_deleting: 'Wird gelöscht…',
    confirm_delete: '{{name}} löschen? Die Bestellungen werden neu zugewiesen. Dies kann nicht rückgängig gemacht werden.',

    // ── Status labels (table pills) ──────────────────────────────────────────
    status_active: 'aktiv',
    status_blocked: 'gesperrt',
    status_invited: 'eingeladen',

    // ── Loading / empty / error ───────────────────────────────────────────────
    loading_users: 'Benutzer werden geladen…',
    empty_title: 'Noch keine Benutzer',
    empty_hint: 'Laden Sie einen Kollegen ein, um zu beginnen.',

    // ── User drawer ───────────────────────────────────────────────────────────
    drawer_close: 'Schließen',
    drawer_since: '· seit',

    // Profile section
    section_profile: 'Name',
    first_name: 'Vorname',
    last_name: 'Nachname',
    btn_save_name: 'Namen speichern',
    btn_saving_name: 'Wird gespeichert…',
    name_saved: 'Name aktualisiert.',

    // Role section
    section_role: 'Rolle',
    role_agent_locked: 'Die Rolle eines Agenten kann nicht geändert werden.',

    // Account section
    section_account: 'Konto',
    btn_reset_password: 'Temporäres Passwort festlegen',
    btn_resetting_password: 'Passwort wird zurückgesetzt…',
    btn_reset_2fa: '2FA zurücksetzen',
    btn_resetting_2fa: '2FA wird zurückgesetzt…',
    tooltip_no_2fa: 'Noch kein 2FA eingerichtet',
    btn_block: 'Benutzer sperren',
    btn_unblock: 'Benutzer entsperren',
    btn_blocking: 'Wird gesperrt…',
    btn_unblocking: 'Wird entsperrt…',
    helper_block: 'Das Sperren widerruft Sitzungen und bewahrt den Verlauf. Zurücksetzungen erzwingen eine erneute Anmeldung.',

    // Prompt / confirm dialogs
    prompt_set_password: 'Legen Sie ein temporäres Passwort für diesen Benutzer fest (er muss es ändern):',
    confirm_reset_2fa: '2FA zurücksetzen? Der Benutzer muss bei der nächsten Anmeldung einen Authenticator neu einrichten.',

    // Notice messages
    notice_password_reset: 'Passwort zurückgesetzt — die Sitzungen des Benutzers wurden widerrufen.',
    notice_2fa_reset: 'Der Benutzer richtet es bei der nächsten Anmeldung neu ein.',

    // ── Invite drawer ─────────────────────────────────────────────────────────
    invite_heading: 'Benutzer hinzufügen',
    invite_close: 'Schließen',
    invite_label_fullname: 'Vollständiger Name',
    invite_label_email: 'E-Mail',
    invite_label_role: 'Rolle',
    invite_hint:
      'Ein temporäres Passwort wird generiert und einmalig angezeigt. Der Benutzer legt bei der ersten Anmeldung sein eigenes Passwort fest und richtet 2FA ein.',
    btn_cancel: 'Abbrechen',
    btn_send_invite: 'Benutzer erstellen',
    btn_sending_invite: 'Wird erstellt…',
    invite_created_heading: 'Benutzer erstellt',
    invite_created_hint: 'Teilen Sie dieses temporäre Passwort mit {{email}} — es wird nur einmal angezeigt.',
    invite_temp_password: 'Temporäres Passwort',
    invite_temp_password_note:
      'Der Benutzer muss es bei der ersten Anmeldung ändern und wird zur Einrichtung von 2FA aufgefordert.',
    invite_done: 'Fertig',
  },
};

export default dict;
